import { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';

const HEIGHT = 140;
const PAD = { top: 8, right: 4, bottom: 20, left: 36 };
const GRID = 'rgba(235,231,225,0.08)';

const compact = (n) => Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(n);

// Clean axis maximum: the next 1 / 2 / 5 × 10ⁿ above the data.
function niceMax(max) {
  if (max <= 0) return 1;
  const pow = 10 ** Math.floor(Math.log10(max));
  return [1, 2, 5, 10].map((m) => m * pow).find((v) => v >= max);
}

// Rounded 4px data-end, square at the baseline.
function barPath(x, y, w, h) {
  const r = Math.min(4, w / 2, h);
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
}

/**
 * Single-series daily column chart with a per-bar hover tooltip.
 * `data`: [{ date: 'YYYY-MM-DD', value: number|null, note?: string }]
 */
export default function BarChart({ title, data, color = '#d27a5a', format = (v) => v.toLocaleString() }) {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setWidth(e.contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  const values = data.map((d) => d.value).filter((v) => v !== null);
  const total = values.reduce((s, v) => s + v, 0);
  const max = niceMax(Math.max(0, ...values));
  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const band = data.length ? plotW / data.length : 0;
  const barW = Math.max(1, Math.min(24, band - 2));
  const y = (v) => PAD.top + plotH - (v / max) * plotH;
  const ticks = [0, max / 2, max];
  const labelEvery = Math.ceil(data.length / Math.max(1, Math.floor(plotW / 56)));
  const hovered = hover !== null ? data[hover] : null;

  return (
    <Box sx={{ p: 1.75, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: 'background.paper', minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={600}>{title}</Typography>
        <Typography variant="caption" color="text.secondary">{format(total)} in period</Typography>
      </Box>
      <Box ref={ref} sx={{ position: 'relative' }} onMouseLeave={() => setHover(null)}>
        {width > 0 && (
          <svg width={width} height={HEIGHT} role="img" aria-label={`${title}: ${format(total)} in period`} style={{ display: 'block' }}>
            {ticks.map((t) => (
              <g key={t}>
                <line x1={PAD.left} x2={width - PAD.right} y1={y(t)} y2={y(t)} stroke={GRID} strokeWidth="1" />
                <text x={PAD.left - 6} y={y(t)} dy="0.32em" textAnchor="end" fontSize="10" fill="#a19c94" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {compact(t)}
                </text>
              </g>
            ))}
            {data.map((d, i) => {
              const x = PAD.left + i * band + (band - barW) / 2;
              const h = d.value ? (d.value / max) * plotH : 0;
              return (
                <g key={d.date}>
                  {d.value > 0 && (
                    <path d={barPath(x, y(d.value), barW, h)} fill={color} opacity={hover === null || hover === i ? (d.partial ? 0.45 : 0.9) : 0.35} />
                  )}
                  {d.value === null && <rect x={x} y={PAD.top + plotH - 1} width={barW} height={1} fill={GRID} />}
                  {i % labelEvery === 0 && (
                    <text x={PAD.left + i * band + band / 2} y={HEIGHT - 6} textAnchor="middle" fontSize="10" fill="#6b6761">
                      {d.date.slice(5).replace('-', '.')}
                    </text>
                  )}
                  {/* Hit target spans the whole band, not just the bar */}
                  <rect x={PAD.left + i * band} y={PAD.top} width={band} height={plotH} fill="transparent" onMouseEnter={() => setHover(i)} />
                </g>
              );
            })}
          </svg>
        )}
        {hovered && (
          <Box sx={{
            position: 'absolute', top: 0, pointerEvents: 'none', zIndex: 1,
            left: Math.min(Math.max(PAD.left + hover * band + band / 2 - 60, 0), Math.max(0, width - 120)),
            width: 120, px: 1, py: 0.75, borderRadius: 1, bgcolor: '#262626', border: '1px solid', borderColor: 'divider',
            fontSize: '0.75rem',
          }}>
            <Box sx={{ color: 'text.secondary' }}>{hovered.date}</Box>
            <Box sx={{ fontWeight: 600 }}>{hovered.value === null ? 'no data' : format(hovered.value)}</Box>
            {hovered.partial && <Box sx={{ color: 'text.secondary' }}>since tracking began</Box>}
          </Box>
        )}
      </Box>
    </Box>
  );
}
